import type { EpisodeListFilters } from "@/api/datasetEpisode";
import { IconButton } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { BookmarkStarButton } from "@/components/buttons";
import { datasetProcessingBadge } from "@/components/dataset";
import { DatasetEpisodeWarningBadge } from "@/components/dataset/DatasetEpisodeWarningBadge";
import { DatasetDetailBody as DatasetDetailBodyView } from "@/components/dataset/detail";
import { datasetStorageBadge } from "@/components/dataset/DatasetStorageBadge";
import { trainingUnsupportedReason } from "@/components/dataset/storageBackendText";
import { DatasetDownloadAction } from "@/components/dataset/download";
import { ArchiveIcon, RefreshIcon, UnarchiveIcon } from "@/components/icons";
import { DisabledReasonTooltip, HatTextTooltip } from "@/components/tooltip";
import { AnnotationStudioTab } from "@/page/annotationStudio/AnnotationStudioTab";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { openTrainingSubmitWindowForDataset } from "@/page/dataset/datasetTrainEntry";
import { datasetDetailModeTab, type DatasetDetailTab } from "./datasetDetailTabs";
import type { DatasetDetailController } from "./useDatasetDetailController";
import styles from "./DatasetDetailContent.module.css";

// 탭 식별(값·정규화)은 leaf 모듈이 소유한다. 그래서 창을 여는 쪽이 렌더 그래프를 import 하지 않는다.
export {
  adjacentDatasetDetailTab,
  datasetDetailModeTab,
  datasetDetailTabs,
  defaultDatasetDetailTab,
  normalizeDatasetDetailTab,
  type DatasetDetailTab,
} from "./datasetDetailTabs";

/**
 * 제목 옆 배지. 모든 탭에서 알아야 하는 사실만 표시한다. 축별 상세는 `메타 정보` 탭이 소유한다.
 * - 저장 위치 칩: 기능 제약이 있는 위치일 때.
 * - 처리 배지: 진행 중·실패일 때.
 * - 채널 누락 버튼: 그런 에피소드가 1개 이상일 때. 누르면 그 에피소드를 연다. 상태 칩 뒤에 둔다.
 *
 * 배지를 액션 클러스터가 아니라 제목 옆에 두는 이유는 둘이다.
 * - Dataset 을 바꾸는 액션이 아니라 상태 표시라서 액션과 성격이 다르다.
 * - 액션 클러스터에 두면 우측 셀이 가득 차서 텍스트 버튼이 밀린다(라벨이 두 줄로 줄바꿈되었다).
 *
 * 이 배지는 컴포넌트가 아니라 함수다. 이것이 계약이다. 배지가 없을 때 호출부가 슬롯에 `null` 을
 * 넣어야 셸이 빈 래퍼를 그리지 않는다(PageBase `.titleAside` 는 래퍼에 gap 을 가진다).
 */
export function datasetDetailTitleBadge(
  controller: DatasetDetailController,
  // 채널 누락 배지를 누르면 부른다. 호스트가 에피소드 탭으로 옮기고 사유 필터를 건다.
  onOpenMissingChannel: () => void,
): ReactNode {
  const dataset = controller.currentDataset;
  if (!dataset) {
    return null;
  }
  // 저장 위치 칩은 기능 제약이 있는 위치에서만 나온다. 탭마다 꺼진 버튼의 이유를 제목에서 알린다.
  const storageBadge = datasetStorageBadge(dataset.storage_backend);
  const processingBadge = datasetProcessingBadge(dataset.processing);
  // 채널 누락 에피소드는 학습에서 빠진다. 어느 탭에서든 알 수 있게 제목에 둔다.
  const missingChannelCount = dataset.missing_channel_episode_count;
  if (storageBadge === null && processingBadge === null && missingChannelCount === 0) {
    return null;
  }
  return (
    <>
      {storageBadge}
      {processingBadge === null ? null : (
        <HatTextTooltip text={processingBadge.title}>
          <span
            className={`${styles.processingBadge} ${
              processingBadge.tone === "danger" ? styles.badgeDanger : styles.badgeProgress
            }`}
          >
            {processingBadge.label}
          </span>
        </HatTextTooltip>
      )}
      <DatasetEpisodeWarningBadge
        kind="missingChannel"
        count={missingChannelCount}
        onOpen={onOpenMissingChannel}
      />
    </>
  );
}

// 제목 우측(탭 위)에 항상 표시하는 데이터셋 액션들. 데이터셋을 적재하기 전에는 그리지 않는다.
// 전체화면 모달은 셸 헤더(headerActions)에 이 컴포넌트를 둔다.
export function DatasetDetailActions({ controller }: { controller: DatasetDetailController }) {
  const dataset = controller.currentDataset;
  if (!dataset) {
    return null;
  }

  const bookmarkLabel = resolveBookmarkButtonTitle({
    isAdded: controller.isCurrentDatasetBookmarked,
    isAdding: controller.isCurrentDatasetBeingBookmarked,
  });

  const archived = dataset.status === "archived";
  const showBookmark = dataset.status === "available" || controller.isCurrentDatasetBookmarked;
  const archiveLabel = archived ? "이 Dataset을 복원합니다." : "이 Dataset을 보관합니다.";
  // 학습 진입은 목록 행 액션과 같은 게이트다. available 데이터셋에만 표시한다. 저장 위치가 학습을
  // 지원하지 않으면 숨기지 않고 끈다.
  const canTrain = dataset.status === "available";
  const trainDisabledReason = trainingUnsupportedReason(dataset);

  return (
    <>
      {/* 학습(텍스트). 클러스터의 첫 버튼. */}
      {!canTrain ? null : trainDisabledReason === null ? (
        <IconButton
          text="Training"
          size="md"
          variant="surface"
          title="이 Dataset으로 새 Training을 시작합니다."
          onClick={() =>
            openTrainingSubmitWindowForDataset({
              uuid: dataset.uuid,
              displayName: dataset.display_name,
            })
          }
        />
      ) : (
        <DisabledReasonTooltip text={trainDisabledReason}>
          <IconButton text="Training" size="md" variant="surface" disabled />
        </DisabledReasonTooltip>
      )}
      {/* 신규 북마크는 available만 가능하다. 이미 담긴 항목은 나중에 보관되어도 선택 상태를 표시한다. */}
      {showBookmark ? (
        <BookmarkStarButton
          aria-label={bookmarkLabel}
          title={bookmarkLabel}
          size="md"
          added={controller.isCurrentDatasetBookmarked}
          disabled={
            controller.isCurrentDatasetBookmarked || controller.isCurrentDatasetBeingBookmarked
          }
          onClick={() => controller.onAddBookmarkClick(dataset)}
        />
      ) : null}
      {/* Refresh(아이콘 only). 상세 summary 를 다시 집계한다. */}
      <IconButton
        aria-label="상세 새로고침"
        title="Dataset 상세 정보를 새로고침합니다."
        size="md"
        variant="surface"
        onClick={controller.onDatasetDetailsRefresh}
      >
        <RefreshIcon />
      </IconButton>
      {/* 데이터셋 전체 다운로드. 폴더를 고르면 원본 트리 그대로 받는다. 유일한 진입점이다.
          Chromium 계열이 아니면 비활성으로 두고 안내 툴팁을 표시한다. */}
      <DatasetDownloadAction datasetUuid={dataset.uuid} />
      {/* archive↔restore(아이콘 only). 맨 끝에 둔다. available/archived 에서만 admin 에게 표시한다. */}
      {controller.canArchive && (dataset.status === "available" || archived) ? (
        <IconButton
          aria-label={archiveLabel}
          title={archiveLabel}
          size="md"
          variant="surface"
          tone={archived ? undefined : "danger"}
          disabled={controller.archiving}
          onClick={() => controller.onArchiveToggleClick(dataset)}
        >
          {archived ? <UnarchiveIcon /> : <ArchiveIcon />}
        </IconButton>
      ) : null}
    </>
  );
}

// 본문(탭 콘텐츠 전용). 어노테이션 탭만 이 계층(page)이 직접 그린다(스튜디오는 page 소유).
// 나머지는 공유 DatasetDetailBody 에 위임한다. 제목·액션은 헤더가 소유한다.
// 모달은 상위가 소유한다.
export function DatasetDetailBody({
  controller,
  activeTab,
  annotationSetUuid,
  episodeFilters,
}: {
  controller: DatasetDetailController;
  activeTab: DatasetDetailTab;
  annotationSetUuid?: string;
  episodeFilters?: EpisodeListFilters;
}) {
  return (
    <>
      <DatasetDetailBodyView
        model={controller}
        activeTab={datasetDetailModeTab(activeTab)}
        episodeFilters={episodeFilters}
      />
      {activeTab !== "annotation" ? null : controller.annotationSupported !== null ? (
        <AnnotationStudioTab
          binding={controller.annotationStudio}
          initialSetUuid={annotationSetUuid}
          unsupportedBackend={controller.annotationUnsupportedBackend}
        />
      ) : // 저장 위치를 알기 전에는 스튜디오를 켜지 않는다. 그동안 스튜디오를 그리면 조회하지 않은 세트
      // 목록이 "없음"으로 보인다. Dataset 을 받지 못하면 이 탭에서 알리고 다시 받게 한다. 다른 탭의
      // 오류 문구는 이 탭에서 가려져 있다.
      controller.dataset.status === "error" ? (
        <EmptyState
          size="lg"
          tone="error"
          action={{
            label: "다시 시도",
            onClick: controller.onDatasetChanged,
            icon: <RefreshIcon />,
          }}
        >
          {controller.dataset.message}
        </EmptyState>
      ) : (
        <LoadingState size="lg">Dataset을 불러오고 있습니다.</LoadingState>
      )}
    </>
  );
}

function resolveBookmarkButtonTitle({
  isAdded,
  isAdding,
}: {
  isAdded: boolean;
  isAdding: boolean;
}): string {
  if (isAdded) {
    return "이미 북마크에 담긴 Dataset입니다.";
  }
  if (isAdding) {
    return "Dataset을 북마크에 담고 있습니다.";
  }
  return "Dataset을 북마크에 담습니다.";
}
